import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import apiClient from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { AppNavbar } from '../components/AppNavbar';
import { ArrowLeft, Loader2, Send, CheckCircle2, Inbox, User as UserIcon, Sparkles } from 'lucide-react';

const formatTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch (_e) { return iso; }
};

const StatusPill = ({ status }) => (
  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
    status === 'closed'
      ? 'bg-gray-100 text-gray-500'
      : 'bg-emerald-100 text-emerald-700'
  }`} data-testid={`ticket-status-${status}`}>
    {status}
  </span>
);

const AdminSupportPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open');
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const res = await apiClient.get('/admin/support/tickets', { params });
      setTickets(res.data?.tickets || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (!user) return;
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchTickets();
  }, [user, isAdmin, fetchTickets, navigate]);

  const openTicket = async (t) => {
    // Refresh from server in case there were appended messages
    try {
      const res = await apiClient.get(`/admin/support/tickets/${t.ticket_id}`);
      setActive(res.data);
    } catch (err) {
      setActive(t);
    }
  };

  const sendReply = async () => {
    if (!reply.trim() || !active) return;
    setSending(true);
    try {
      await apiClient.post(`/admin/support/tickets/${active.ticket_id}/reply`, { body: reply });
      toast.success('Reply sent — customer notified by email');
      setReply('');
      await fetchTickets();
      const refreshed = await apiClient.get(`/admin/support/tickets/${active.ticket_id}`);
      setActive(refreshed.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const closeTicket = async () => {
    if (!active) return;
    if (!window.confirm('Close this ticket?')) return;
    try {
      await apiClient.post(`/admin/support/tickets/${active.ticket_id}/close`);
      toast.success('Ticket closed');
      setActive(null);
      fetchTickets();
    } catch (err) {
      toast.error('Failed to close ticket');
    }
  };

  const suggestReply = async () => {
    if (!active || suggesting) return;
    setSuggesting(true);
    try {
      const res = await apiClient.post(`/admin/support/tickets/${active.ticket_id}/suggest`);
      const draft = res.data?.draft || '';
      if (!draft) {
        toast.error('No suggestion returned');
        return;
      }
      // If the reply box already has content, append; otherwise replace
      setReply(prev => prev.trim() ? `${prev}\n\n${draft}` : draft);
      toast.success('AI draft inserted — edit and send');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI suggestion failed');
    } finally {
      setSuggesting(false);
    }
  };

  if (!user) return null;
  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-support-page">
      <AppNavbar />

      <main className="container mx-auto px-6 py-8 max-w-6xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              Support Inbox
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Tickets escalated from the chat widget. Reply here — customers receive an email.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="admin-support-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Dashboard
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-4">
          {['open', 'closed', 'all'].map(s => (
            <button
              key={s}
              onClick={() => { setFilter(s); setActive(null); }}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize ${
                filter === s ? 'bg-black text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-6">
          {/* Ticket list */}
          <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
            <div className="px-3 py-2 border-b border-gray-100 text-xs uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1.5">
              <Inbox className="h-3.5 w-3.5" /> {tickets.length} {filter !== 'all' ? filter : ''} ticket{tickets.length === 1 ? '' : 's'}
            </div>
            {loading ? (
              <div className="p-6 text-center text-sm text-gray-400 flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : tickets.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-400" data-testid="tickets-empty-state">
                No tickets — you're all caught up.
              </div>
            ) : (
              <div className="max-h-[60vh] overflow-y-auto divide-y divide-gray-100">
                {tickets.map(t => (
                  <button
                    key={t.ticket_id}
                    onClick={() => openTicket(t)}
                    className={`w-full text-left px-3 py-3 hover:bg-gray-50 transition-colors ${
                      active?.ticket_id === t.ticket_id ? 'bg-amber-50' : ''
                    }`}
                    data-testid={`ticket-row-${t.ticket_id}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold truncate">{t.subject || 'Support request'}</span>
                      <StatusPill status={t.status} />
                    </div>
                    <p className="text-xs text-gray-500 truncate">{t.contact_email}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">{formatTime(t.created_at)}</p>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Active ticket detail */}
          <div className="border border-gray-200 rounded-lg bg-white min-h-[400px] flex flex-col">
            {!active ? (
              <div className="flex-1 flex items-center justify-center text-sm text-gray-400" data-testid="ticket-empty-detail">
                Select a ticket to view the conversation
              </div>
            ) : (
              <>
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                  <div>
                    <h2 className="font-semibold">{active.subject || 'Support request'}</h2>
                    <p className="text-xs text-gray-500">
                      {active.contact_name ? `${active.contact_name} · ` : ''}{active.contact_email}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusPill status={active.status} />
                    {active.status !== 'closed' && (
                      <Button variant="outline" size="sm" onClick={closeTicket} data-testid="ticket-close-btn">
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Close
                      </Button>
                    )}
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50" data-testid="ticket-transcript">
                  {(active.messages || []).length === 0 && (
                    <p className="text-center text-xs text-gray-400">No prior messages — escalated directly.</p>
                  )}
                  {(active.messages || []).map((m, i) => {
                    const bubble =
                      m.role === 'admin' ? 'bg-amber-100 text-amber-900' :
                      m.role === 'assistant' ? 'bg-white border border-gray-200 text-gray-800 shadow-sm' :
                      'bg-black text-white';
                    const label =
                      m.role === 'admin' ? 'You' :
                      m.role === 'assistant' ? 'AI Bot' :
                      'Customer';
                    const align = m.role === 'admin' || m.role === 'assistant' ? 'justify-start' : 'justify-end';
                    return (
                      <div key={i} className={`flex ${align}`}>
                        <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${bubble}`}>
                          <p className="text-[10px] font-bold opacity-60 uppercase tracking-wider mb-0.5">{label}</p>
                          {m.content}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {active.status !== 'closed' && (
                  <div className="border-t border-gray-100 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <button
                        onClick={suggestReply}
                        disabled={suggesting}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r from-amber-400 to-amber-600 text-white shadow-sm hover:shadow-md disabled:opacity-50 transition-all"
                        data-testid="ticket-ai-suggest"
                      >
                        {suggesting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                        {suggesting ? 'Drafting…' : 'AI suggested reply'}
                      </button>
                      <p className="text-[10px] text-gray-400">⌘+Enter to send</p>
                    </div>
                    <div className="flex items-end gap-2">
                      <textarea
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) sendReply(); }}
                        rows={3}
                        placeholder="Reply to customer (sends an email)…"
                        className="flex-1 resize-none px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black/40"
                        data-testid="ticket-reply-input"
                      />
                      <Button onClick={sendReply} disabled={!reply.trim() || sending} data-testid="ticket-reply-send">
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                        Reply
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminSupportPage;
